// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Master API client (Stage 6B.3).
//
// Mirrors customerApi shape: POST /list for the grid (so nested
// advancedFilters survive), camelCase → snake_case bridge for grid
// fields, header-based Approval-MFA challenge id for delete /
// non-active status changes.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';

const BASE = '/api/erp/masters/suppliers';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

// camelCase grid field → snake_case backend whitelist key.
const GRID_FIELD_TO_SQL = {
  supplierCode:           'supplier_code',
  supplierName:           'supplier_name',
  displayName:            'display_name',
  supplierCategory:       'supplier_category',
  // Stage 6B.3B — supplier group fields routed to the JOINed sg.* keys
  // declared in the backend whitelist.
  supplierGroupId:        'supplier_group_id',
  supplierGroupName:      'supplier_group_name',
  supplierGroupCode:      'supplier_group_code',
  supplierType:           'supplier_type',
  gstin:                  'gstin',
  pan:                    'pan',
  gstRegistrationType:    'gst_registration_type',
  gstStateCode:           'gst_state_code',
  stateCode:              'state_code',
  state:                  'state',
  city:                   'city',
  mobile:                 'mobile',
  email:                  'email',
  status:                 'status',
  isActive:               'is_active',
  msmeRegistered:         'msme_registered',
  creditDays:             'credit_days',
  openingBalance:         'opening_balance',
  createdAt:              'created_at',
  updatedAt:              'updated_at'
};

function mapField(camel) {
  if (!camel || typeof camel !== 'string') return camel;
  return GRID_FIELD_TO_SQL[camel] || camel;
}

function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters
      .filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort
      .filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) {
    out.groupBy = mapField(out.groupBy);
  }
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: translateAdvancedRules(out.advancedFilters.rules)
    };
  }
  return out;
}

function translateAdvancedRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: translateAdvancedRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}

export const supplierApi = {
  list: (query) =>
    client.post(`${BASE}/list`, translateQuery(query) || {}).then(unwrap),
  get: (id) =>
    client.get(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create: (payload) =>
    client.post(BASE, payload).then(unwrap),
  update: (id, payload) =>
    client.put(`${BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  updateStatus: (id, body, { approvalMfaChallengeId } = {}) =>
    client.patch(
      `${BASE}/${encodeURIComponent(id)}/status`,
      approvalMfaChallengeId ? { ...body, approvalMfaChallengeId } : body
    ).then(unwrap),
  updateCell: (id, body) =>
    client.patch(`${BASE}/${encodeURIComponent(id)}/cell`, body).then(unwrap),
  remove: (id, { approvalMfaChallengeId } = {}) =>
    client.delete(`${BASE}/${encodeURIComponent(id)}`, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  gstinSearch: (gstin) =>
    client.post(`${BASE}/gstin-search`, { gstin }).then(unwrap)
};

// ─────────────────────────────────────────────────────────────────
// Stage 6B.3B — Supplier Group (Supplier Category) CRUD API.
//
// Mounted under /api/erp/masters/suppliers/groups so it shares the
// auth + tenant + rate-limit middleware chain with the supplier
// surface. The lookup endpoint is what SupplierModal's dropdown
// consumes.
// ─────────────────────────────────────────────────────────────────
const GROUPS_BASE = `${BASE}/groups`;

export const supplierGroupApi = {
  list: (params = {}) =>
    client.get(GROUPS_BASE, { params }).then(unwrap),
  lookup: (q = '', limit = 200) =>
    client.get(`${GROUPS_BASE}/lookup`, { params: { q, limit } })
      .then(unwrap)
      .then((data) => (data && Array.isArray(data.rows) ? data.rows : [])),
  get: (id) =>
    client.get(`${GROUPS_BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create: (payload) =>
    client.post(GROUPS_BASE, payload).then(unwrap),
  update: (id, payload) =>
    client.put(`${GROUPS_BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  remove: (id) =>
    client.delete(`${GROUPS_BASE}/${encodeURIComponent(id)}`).then(unwrap)
};

export default supplierApi;
